import { useState, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  LayoutDashboard,
  Wrench,
  Users,
  UserCircle2,
  Store,
  BarChart3,
  Truck,
  Tag,
  BookOpen,
  Layers,
  X } from
"lucide-react";
import { cn } from "../../lib/utils";
import { buildNavTree, cleanName, isPathActive } from "../../router/navigation";
import { getServiceMeta } from "../../utils/serviceColors";
import { useTranslation } from "react-i18next";

/** Meaningful icon per known service, keyed to serviceColors.js's KNOWN map. */
const SERVICE_ICONS = {
  FSM: Wrench,
  HRMS: Users,
  ESS: UserCircle2,
  CRM: Store,
  ERP: BarChart3,
  SCM: Truck,
  POS: Tag,
  LMS: BookOpen
};

/**
 * svc.name can be a compound label like "HRMS Team" or "HRMS ESS" rather
 * than a bare code, so match a known key against individual words (checked
 * last-to-first, since the trailing word tends to be the more specific one,
 * e.g. "ESS" in "HRMS ESS") before falling back to a plain substring check.
 */
function getServiceIcon(name) {
  if (!name) return Layers;
  const words = String(name).trim().toUpperCase().split(/\s+/);

  for (let i = words.length - 1; i >= 0; i -= 1) {
    if (SERVICE_ICONS[words[i]]) return SERVICE_ICONS[words[i]];
  }

  const upper = words.join(" ");
  const matchedKey = Object.keys(SERVICE_ICONS).find((key) => upper.includes(key));
  return matchedKey ? SERVICE_ICONS[matchedKey] : Layers;
}

function isLegacyDashboardLink(to) {
  return typeof to === "string" && to.startsWith("/dashboard.html");
}

/**
 * Sidebar is rendered entirely from the session's enabledServices tree
 * (see router/navigation.js): one collapsible group per service, one
 * collapsible sub-group per module, and a link per submodule. Clicking a
 * submodule routes to its dedicated page when one exists, or to the generic
 * submodule features page otherwise — the same resolution WelcomePage uses.
 *
 * A single <aside> serves both breakpoints: static panel on md+ (with the
 * icon-only collapse mode), and an overlay drawer below md toggled by the
 * header's "toggle-sidebar" event. The drawer uses the same collapsible
 * service/module tree as desktop. In icon-only mode, clicking a service
 * icon opens a flyout (same drill-down as Horizontalbar) instead of
 * expanding inline.
 *
 * Expansion is accordion-style at each level: opening a service collapses
 * any other open service (and its modules); opening a module collapses any
 * other open module within the same service.
 */
export function Sidebar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const navTree = useMemo(() => buildNavTree(), []);

  // Desktop icon-only collapse state (persisted)
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("hrms_sidebar_collapsed") === "true";
    }
    return false;
  });

  // Mobile overlay drawer state
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Effective collapse: the mobile drawer always shows full labels.
  const collapsed = isPanelCollapsed && !isMobileOpen;
  const asideRef = useRef(null);
  const flyoutRef = useRef(null);
  const [flyoutService, setFlyoutService] = useState(null);
  const [flyoutModule, setFlyoutModule] = useState(null);
  const [flyoutPosition, setFlyoutPosition] = useState(null);

  /**
   * Expanded state per group, keyed "service" and "service::module".
   * Groups containing the active route start expanded.
   */
  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    navTree.forEach((svc) => {
      svc.modules.forEach((mod) => {
        if (mod.submodules.some((sub) => isPathActive(pathname, sub.to))) {
          initial[svc.name] = true;
          initial[`${svc.name}::${mod.name}`] = true;
        }
      });
    });
    return initial;
  });

  // Header hamburger dispatches this on <md screens
  useEffect(() => {
    const handleToggleMobile = () => {
      setIsMobileOpen((prev) => !prev);
    };
    window.addEventListener("toggle-sidebar", handleToggleMobile);
    return () => {
      window.removeEventListener("toggle-sidebar", handleToggleMobile);
    };
  }, []);

  // Close the drawer and any collapsed flyout after navigating
  useEffect(() => {
    setIsMobileOpen(false);
    setFlyoutService(null);
    setFlyoutModule(null);
    setFlyoutPosition(null);
  }, [pathname]);

  useEffect(() => {
    if (!collapsed) {
      setFlyoutService(null);
      setFlyoutModule(null);
      setFlyoutPosition(null);
    }
  }, [collapsed]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (
        !flyoutRef.current?.contains(event.target) &&
        !asideRef.current?.contains(event.target)
      ) {
        setFlyoutService(null);
        setFlyoutModule(null);
        setFlyoutPosition(null);
      }
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, []);

  const togglePanel = () => {
    setIsPanelCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("hrms_sidebar_collapsed", String(next));
      setTimeout(() => window.dispatchEvent(new Event("resize")), 100);
      return next;
    });
  };

  const closeFlyout = () => {
    setFlyoutService(null);
    setFlyoutModule(null);
    setFlyoutPosition(null);
  };

  const openCollapsedFlyout = (svcName, anchorEl) => {
    if (flyoutService === svcName) {
      closeFlyout();
      return;
    }
    const rect = anchorEl?.getBoundingClientRect();
    if (rect) {
      const menuWidth = 272;
      const gap = 8;
      const preferredMax = Math.min(window.innerHeight * 0.7, 512);
      let left = rect.right + gap;
      if (left + menuWidth > window.innerWidth - gap) {
        left = Math.max(gap, rect.left - menuWidth - gap);
      }

      const spaceBelow = window.innerHeight - rect.top - gap;
      const spaceAbove = rect.bottom - gap;
      // Stay level with the icon. If the icon is too low for the menu to
      // open downward, grow upward from the icon instead of jumping to the
      // top of the viewport.
      const openUpward = spaceBelow < 200 && spaceAbove > spaceBelow;
      setFlyoutPosition(
        openUpward
          ? {
              left,
              bottom: window.innerHeight - rect.bottom,
              maxHeight: Math.min(preferredMax, Math.max(spaceAbove, 120)),
            }
          : {
              left,
              top: rect.top,
              maxHeight: Math.min(preferredMax, Math.max(spaceBelow, 120)),
            }
      );
    }
    setFlyoutService(svcName);
    setFlyoutModule(null);
  };

  // Level 1: opening a service closes any other open service (and its
  // modules); re-clicking an open service just closes it. In icon-only
  // mode the children open in a flyout instead of inline.
  const toggleService = (svcName, event) => {
    if (collapsed) {
      openCollapsedFlyout(svcName, event?.currentTarget);
      return;
    }
    setExpanded((prev) => {
      const isOpen = !!prev[svcName];
      if (isOpen) {
        const next = { ...prev };
        delete next[svcName];
        Object.keys(next).forEach((k) => {
          if (k.startsWith(`${svcName}::`)) delete next[k];
        });
        return next;
      }
      return { [svcName]: true };
    });
  };

  // Level 2: opening a module closes any other open module within the same
  // service; re-clicking an open module just closes it.
  const toggleModule = (svcName, modName) => {
    const modKey = `${svcName}::${modName}`;
    setExpanded((prev) => {
      const isOpen = !!prev[modKey];
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        if (k.startsWith(`${svcName}::`)) delete next[k];
      });
      if (!isOpen) {
        next[modKey] = true;
      }
      return next;
    });
  };

  const navItemClass = (active) =>
  cn(
    "w-full flex items-center justify-between rounded-xl text-sm font-medium transition-all text-left cursor-pointer",
    collapsed ? "px-2.5 py-2.5 justify-center" : "px-3.5 py-2.5",
    active ?
    "bg-[var(--color-accent)] text-white shadow-[0_8px_20px_-8px_rgba(61,90,241,0.7)]" :
    "text-white/70 hover:bg-white/5 hover:text-white"
  );

  /**
   * The full Service -> Module -> Submodule tree. Same collapsible groups
   * (with chevrons) on desktop and in the mobile drawer.
   */
  const renderNavTree = () =>
  navTree.map((svc) => {
    const meta = getServiceMeta(svc.name);
    const ServiceIcon = getServiceIcon(svc.name);
    const svcOpen = expanded[svc.name];
    const flyoutOpen = collapsed && flyoutService === svc.name;
    const serviceActive = svc.modules.some((mod) =>
      mod.submodules.some((sub) => isPathActive(pathname, sub.to))
    );

    return (
      <div key={svc.name} className="space-y-1">
          {/* Level 1: Service — the only level that carries an icon, mapped
             to something meaningful per service (see SERVICE_ICONS above). */}
          <button
            type="button"
            onClick={(event) => toggleService(svc.name, event)}
            className={cn(
              "w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-semibold text-white hover:bg-white/5 transition text-left cursor-pointer",
              collapsed && "justify-center px-1",
              collapsed && (flyoutOpen || serviceActive) && "bg-white/15"
            )}
            title={svc.name}
            aria-expanded={collapsed ? flyoutOpen : !!svcOpen}
            aria-haspopup={collapsed ? "menu" : undefined}>
          
              <div className="flex items-center gap-3">
                <ServiceIcon className="h-[18px] w-[18px] shrink-0" style={{ color: meta.color }} />
                {!collapsed && <span>{svc.name}</span>}
              </div>
              {!collapsed && (
          svcOpen ?
          <ChevronDown className="h-4 w-4 text-white/50" /> :

          <ChevronRight className="h-4 w-4 text-white/50" />)
          }
            </button>

          {/* Level 2: Modules — text only, no icon */}
          {svcOpen && !collapsed &&
        <div className="pl-4 ml-3 border-l border-white/10 space-y-1">
              {svc.modules.map((mod) => {
            const modKey = `${svc.name}::${mod.name}`;
            const modOpen = expanded[modKey];

            return (
              <div key={mod.name} className="space-y-1">
                    <button
                      type="button"
                      onClick={() => toggleModule(svc.name, mod.name)}
                      className="w-full flex items-center justify-between px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white/90 hover:bg-white/5 transition text-left cursor-pointer">
                  
                        <span>{cleanName(mod.name)}</span>
                        {modOpen ?
                  <ChevronDown className="h-4 w-4 text-white/40 shrink-0" /> :

                  <ChevronRight className="h-4 w-4 text-white/40 shrink-0" />
                  }
                      </button>

                    {/* Level 3: Submodules -> route links — text only, no icon */}
                    {modOpen &&
                <div className="pl-4 ml-3 border-l border-white/10 space-y-1">
                        {mod.submodules.map((sub) => {
                    const active = isPathActive(pathname, sub.to);
                    const className = navItemClass(active);

                    if (isLegacyDashboardLink(sub.to)) {
                      return (
                        <a key={sub.name} href={sub.to} className={className}>
                                <span>{cleanName(sub.name)}</span>
                              </a>);

                    }

                    return (
                      <Link
                        key={sub.name}
                        to={sub.to}
                        className={className}>
                        
                              <span>{cleanName(sub.name)}</span>
                            </Link>);

                  })}
                      </div>
                }
                  </div>);

          })}
            </div>
        }
        </div>);

  });

  const flyoutSvc = collapsed && flyoutService
    ? navTree.find((svc) => svc.name === flyoutService)
    : null;
  const flyoutMod = flyoutSvc && flyoutModule
    ? flyoutSvc.modules.find((mod) => mod.name === flyoutModule)
    : null;

  return (
    <>
      {/* Mobile drawer backdrop */}
      {isMobileOpen &&
      <div
        onClick={() => setIsMobileOpen(false)}
        className="fixed inset-x-0 bottom-0 top-[100px] z-40 bg-[#0a1230]/50 backdrop-blur-sm sm:top-[68px] md:hidden" />

      }

      <aside
        ref={asideRef}
        className={cn(
          "hrms-shell__sidebar flex-col shrink-0 text-white px-4 pb-4 pt-0",
          // <md: overlay drawer, hidden unless toggled open
          isMobileOpen ? "hrms-shell__sidebar--open flex fixed bottom-0 left-0 top-[100px] z-50 sm:top-[68px]" : "hidden",
          // md+: always visible, static panel
          "md:flex md:static md:inset-auto md:z-auto md:transition-all md:duration-300"
        )}
        style={{ width: collapsed ? "76px" : "260px" }}>
        
        <div className="flex flex-col h-full min-h-0">
          {isMobileOpen &&
          <div className="mb-2 flex h-12 shrink-0 items-center justify-end border-b border-white/10 px-1 md:hidden">
            <button
              type="button"
              onClick={() => setIsMobileOpen(false)}
              className="h-8 w-8 shrink-0 rounded-lg border border-white/20 grid place-items-center text-white/80 hover:text-white md:hidden"
              aria-label={t("common.closeMenu")}>
              
                <X className="h-4 w-4" />
              </button>
          </div>
          }
          <nav className="space-y-4 flex-1 overflow-y-auto pr-1 pt-2">
            <Link to="/" onClick={closeFlyout} className={navItemClass(pathname === "/")}>
              <div className="flex items-center gap-3">
                <LayoutDashboard className="h-[18px] w-[18px] shrink-0" />
                {!collapsed && <span>{t("common.dashboard")}</span>}
              </div>
            </Link>

            {renderNavTree()}

            {navTree.length === 0 && !collapsed &&
            <p className="px-3.5 text-xs text-white/40">
                {t("common.noServices")}
              </p>
            }
          </nav>

          {/* User profile & Sign Out block
            <div className={cn("pt-4 mt-4 border-t border-white/10 flex gap-2 min-w-0", collapsed ? "flex-col items-center" : "flex-row items-center justify-between")}>
             <div className={cn("flex items-center gap-3 px-3.5 py-2 rounded-xl bg-white/5 border border-white/5 min-w-0", collapsed ? "justify-center px-1" : "flex-1")}>
               <div className="h-9 w-9 rounded-full bg-[var(--color-primary)] border border-white/20 flex items-center justify-center font-bold text-white text-xs shrink-0" title={collapsed ? `${emp.first_name || ""} ${emp.last_name || ""}` : undefined}>
                 {initials}
               </div>
               {!collapsed && (
                 <div className="flex flex-col min-w-0">
                   <span className="text-sm font-semibold text-white truncate">
                     {emp.first_name || ""} {emp.last_name || ""}
                   </span>
                   <span className="text-[10px] text-white/50 truncate">
                     {emp.designation_name || "Employee"}
                   </span>
                 </div>
               )}
             </div>
              <button
               type="button"
               onClick={handleLogout}
               className={cn(
                 "rounded-xl flex items-center justify-center text-rose-300 hover:bg-rose-500/10 hover:text-rose-200 transition cursor-pointer bg-white/5 border border-white/5 shrink-0",
                 collapsed ? "h-9 w-9 border-0 bg-transparent hover:bg-white/5" : "h-11 w-11"
               )}
               title="Sign Out"
             >
               <LogOut className="h-[18px] w-[18px] shrink-0" />
             </button>
            </div> */
          }

          {/* Collapse toggle (both desktop and mobile) */}
          <div className="pt-4 mt-4 border-t border-white/10">
            <button
              type="button"
              onClick={isMobileOpen ? () => setIsMobileOpen(false) : togglePanel}
              className={cn(
                "w-full flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold text-white/70 hover:bg-white/5 hover:text-white transition cursor-pointer",
                collapsed && "justify-center px-1"
              )}>
              
              <ChevronLeft
                className={cn(
                  "h-[18px] w-[18px] transition-transform shrink-0",
                  (collapsed || isMobileOpen) && "rotate-0",
                  collapsed && !isMobileOpen && "rotate-180"
                )} />
              
              {(!collapsed || isMobileOpen) && <span>{t("common.collapsePanel")}</span>}
            </button>
          </div>
        </div>
      </aside>

      {flyoutSvc && flyoutPosition && createPortal(
        <div
          ref={flyoutRef}
          className="fixed z-[60] min-w-64 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 shadow-xl"
          style={flyoutPosition}
          role="menu">
          
          {!flyoutMod ?
          <>
              <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {flyoutSvc.name}
              </p>
              {flyoutSvc.modules.map((module) =>
            <button
              key={module.name}
              type="button"
              onClick={() => setFlyoutModule(module.name)}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-100">
              
                  {cleanName(module.name)}
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </button>
            )}
            </> :

          <>
              <button
              type="button"
              onClick={() => setFlyoutModule(null)}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-100">
              
                <ChevronLeft className="h-4 w-4" />
                {flyoutSvc.name}
              </button>
              <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {cleanName(flyoutMod.name)}
              </p>
              {flyoutMod.submodules.map((submodule) => {
              const active = isPathActive(pathname, submodule.to);
              const className = cn(
                "block rounded-lg px-3 py-2 text-sm transition",
                active ? "bg-[var(--color-accent)] text-white" : "text-slate-700 hover:bg-slate-100"
              );
              return isLegacyDashboardLink(submodule.to) ?
              <a key={submodule.name} href={submodule.to} className={className}>
                    {cleanName(submodule.name)}
                  </a> :

              <Link key={submodule.name} to={submodule.to} className={className}>
                    {cleanName(submodule.name)}
                  </Link>;

            })}
            </>
          }
        </div>,
        document.body)}
    </>);

}
